import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Loader2 } from 'lucide-react';
import documentsApi from '../../../api/documentsApi';
import type { DocInvoice } from '../../../api/types';
import { formatUploadDate } from '../../../utils/formatDate';
import { getDocumentVendorName } from '../../../utils/getDocumentVendorName';

export interface RelatedTransactionItem extends DocInvoice {
    _doc_type?: 'invoice' | 'bill';
    _s3_filename?: string;
}

interface RelatedTransactionsModalProps {
    fileId: string;
    onClose: () => void;
    businessId?: string;
}

const RelatedTransactionsModal: React.FC<RelatedTransactionsModalProps> = ({
    fileId,
    onClose,
    businessId,
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [items, setItems] = useState<RelatedTransactionItem[]>([]);

    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await documentsApi.getRelatedTransactionsByFile(fileId);
                if (mounted) setItems(res.items || []);
            } catch (err: any) {
                if (mounted) setError(err.message || 'Failed to load related documents');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetch();
        return () => { mounted = false; };
    }, [fileId]);

    const handleItemClick = async (item: RelatedTransactionItem) => {
        const s3Filename = item._s3_filename;
        if (!s3Filename) {
            return; // No S3 file to open
        }
        try {
            const res = await documentsApi.getDownloadUrl(s3Filename, true, businessId);
            if (res?.url) {
                window.open(res.url, '_blank');
            }
        } catch (err: any) {
            console.error('Failed to open document:', err);
        }
    };

    const content = (
        <div
            className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Related document</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                            <p className="text-sm text-gray-500">Loading…</p>
                        </div>
                    ) : error ? (
                        <p className="text-sm text-red-600 py-4">{error}</p>
                    ) : items.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">No other invoices or bills from this vendor.</p>
                    ) : (
                        <ul className="space-y-2">
                            {items.map((item) => {
                                const docId = item._id ?? item.id;
                                const docType = item._doc_type === 'bill' ? 'bill' : 'invoice';
                                const vendor = getDocumentVendorName(item, 'Unknown') || 'Unknown';
                                const invoiceNumber = item.data?.invoice_number || '—';
                                const amount = item.data?.total_amount;
                                const date = item.data?.transaction_date;
                                return (
                                    <li key={`${docType}-${docId}`}>
                                        <button
                                            type="button"
                                            onClick={() => handleItemClick(item)}
                                            disabled={!item._s3_filename}
                                            title={item._s3_filename ? 'Open document in new tab' : 'File not available'}
                                            className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors group disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-gray-200"
                                        >
                                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                                <FileText className="w-5 h-5 text-blue-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {vendor}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {invoiceNumber} • {date ? formatUploadDate(String(date)) : '—'} • {amount != null ? `$${amount.toFixed(2)}` : '—'}
                                                </p>
                                            </div>
                                            <span className="flex-shrink-0 text-xs font-medium px-2 py-1 rounded bg-gray-100 text-gray-600">
                                                {docType === 'bill' ? 'Bill' : 'Invoice'}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(content, document.body);
};

export default RelatedTransactionsModal;
